import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import PaymentTrustBadges from '../components/PaymentTrustBadges';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, X, MapPin, Clock, Truck, Store, ArrowLeft, CreditCard, MessageSquare, Phone, ChevronRight, Star, Mail, MessageCircle, AlertCircle } from 'lucide-react';
import { useTranslatedTexts } from '../hooks/useTranslatedTexts';
import ItemDetailModal from '../components/ItemDetailModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import FoodImage from '../components/FoodImage';
import FavoriteHeartButton from '../components/FavoriteHeartButton';
import SEO from '../components/SEO';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { getOpenStatus } from '../utils/businessHours';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Cart persistence ─────────────────────────────────────────────────────
// Keep the customer's cart alive for up to 2 hours per restaurant slug. A
// closed tab, accidental refresh, or language switch should NEVER cost an
// order. Stored under a per-slug key so multiple restaurants stay isolated.
const CART_TTL_MS = 2 * 60 * 60 * 1000;            // 2h
const cartStorageKey = (slug) => `nofee_cart_${slug}`;

const loadCartFromStorage = (slug) => {
  try {
    const raw = localStorage.getItem(cartStorageKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > CART_TTL_MS) {
      // Expired — clean up so we never restore stale data.
      localStorage.removeItem(cartStorageKey(slug));
      return null;
    }
    return parsed.items;
  } catch (_e) {
    return null;
  }
};

const saveCartToStorage = (slug, items) => {
  try {
    if (!Array.isArray(items) || items.length === 0) {
      localStorage.removeItem(cartStorageKey(slug));
      return;
    }
    localStorage.setItem(
      cartStorageKey(slug),
      JSON.stringify({ items, savedAt: Date.now() }),
    );
  } catch (_e) { /* quota or private-mode — fail silently */ }
};

const clearCartFromStorage = (slug) => {
  try { localStorage.removeItem(cartStorageKey(slug)); } catch (_e) { /* ignore */ }
};

// iter91 — full US state list (50 + DC) for the checkout State dropdown.
// Kept at module scope so React never re-allocates it on render.
const US_STATES = [
  { code: 'AL', name: 'Alabama' },        { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },        { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },     { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },    { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },        { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },         { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },       { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },           { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },       { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },          { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },      { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },       { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },       { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },     { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },           { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },         { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },   { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },   { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },          { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },        { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },     { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },      { code: 'WY', name: 'Wyoming' },
];

const PublicMenu = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [restaurant, setRestaurant] = useState(null);
  const [taxInfo, setTaxInfo] = useState({ rate_pct: 0, state: '' });
  // Restaurant's enabled+visible order-receiving channels — drives which
  // notify options (WhatsApp / SMS / Email) the customer is offered at
  // checkout. Loaded from the public /order-channels endpoint so we never
  // leak internal-only channels.
  const [restaurantChannels, setRestaurantChannels] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  // Confirmation overlay for "Start New Order" — kept separate from
  // showCart so the parent review modal stays mounted underneath while
  // the customer decides. Default false; flipped by the cart-clear CTA.
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [reviews, setReviews] = useState({ count: 0, average: 0, items: [] });
  const [showReviews, setShowReviews] = useState(false);
  // Holiday Hours — server-side derived "open now" status that combines
  // regular business_hours with active special_hours overrides.
  const [hoursToday, setHoursToday] = useState(null);

  // Checkout form
  //
  // iter91 — `delivery_address` is no longer a single freeform string.
  // It's composed at submit time from FOUR structured sub-fields
  // (`delivery_street`, `delivery_city`, `delivery_state`, `delivery_zip`)
  // so we can enforce all four are filled and the ZIP↔city/state combo
  // is consistent. The backend keeps receiving a single `delivery_address`
  // string for backwards compatibility — composition happens client-side.
  const [checkout, setCheckout] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    order_type: 'delivery',
    delivery_address: '',
    delivery_street: '', delivery_city: '', delivery_state: '', delivery_zip: '',
    payment_method: 'card', notes: '', notify_channels: [],
  });
  // ZIP → city/state lookup state for the structured delivery address.
  // Mirrors the same UX from the customer signup ZIP field.
  const [deliveryZipLookup, setDeliveryZipLookup] = useState({ status: 'idle', city: '', state: '', error: '' });
  const [tipPercent, setTipPercent] = useState(null); // null | 15 | 18 | 20 | 25 | 'custom'
  const [customTip, setCustomTip] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Delivery-radius eligibility — populated by an effect that watches
  // `delivery_address` and calls the restaurant's `/delivery-eligibility`
  // endpoint as soon as a ZIP is detected. When `eligible:false` the
  // Pay button is disabled and a friendly error renders inline under
  // the address input. `null` = not yet checked / no ZIP yet.
  const [deliveryEligibility, setDeliveryEligibility] = useState(null);
  // iter92 — saved addresses for signed-in customers. Pre-fills checkout
  // with the default saved address so the user doesn't have to retype on
  // every order, but stays editable via the "Change address" toggle so
  // they can still deliver to a different location (work, friend, etc.).
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [usingSavedAddress, setUsingSavedAddress] = useState(false);
  const [activeSavedAddrId, setActiveSavedAddrId] = useState(null);
  // Per-field validation errors. Keys match `checkout` fields plus a synthetic
  // `_blocker` key for restaurant/global-level problems (closed, payments down,
  // min order, etc.). Cleared on every keystroke for the touched field.
  const [errors, setErrors] = useState({});
  const fieldRefs = React.useRef({});

  useEffect(() => {
    loadRestaurant();
  }, [slug]);

  // iter92 — fetch the signed-in customer's saved profile + addresses ONCE
  // on mount and pre-fill the checkout. Anonymous customers see an empty
  // form (unchanged). Authenticated customers see their default address
  // already filled in plus a "Change address" toggle if they need to
  // deliver elsewhere (work, friend's place, etc.).
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [meRes, addrRes] = await Promise.all([
          axios.get(`${API}/api/customers/me`, { headers }).catch(() => null),
          axios.get(`${API}/api/customer/account/addresses`, { headers }).catch(() => null),
        ]);
        if (cancelled) return;
        const me = meRes?.data || null;
        const list = Array.isArray(addrRes?.data?.addresses) ? addrRes.data.addresses : [];
        setSavedAddresses(list);
        // Pre-fill name / phone / email even if no saved address exists.
        if (me) {
          setCheckout((c) => ({
            ...c,
            customer_name:  c.customer_name  || me.name  || '',
            customer_phone: c.customer_phone || me.phone || '',
            customer_email: c.customer_email || me.email || '',
          }));
        }
        // Pick the default saved address (or fall back to the profile-level
        // address fields the customer filled during onboarding).
        const def =
          list.find((a) => a.is_default) ||
          list[0] ||
          (me?.address
            ? { id: 'profile', label: 'My address', address: me.address, city: me.city || '', state: (me.state || '').toUpperCase(), zip_code: me.zip_code || '' }
            : null);
        if (def && def.address) {
          setCheckout((c) => ({
            ...c,
            delivery_street: c.delivery_street || def.address || '',
            delivery_city:   c.delivery_city   || def.city    || '',
            delivery_state:  c.delivery_state  || (def.state || '').toUpperCase(),
            delivery_zip:    c.delivery_zip    || def.zip_code || '',
          }));
          setActiveSavedAddrId(def.id || 'profile');
          setUsingSavedAddress(true);
        }
      } catch {
        /* soft-fail: anonymous flow continues to work */
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // ── Restore saved cart on first visit for this slug ──────────────────
  // Critical UX guarantees (2026-06-03):
  //   1. The "Welcome back" toast fires AT MOST ONCE per browser tab session
  //      per slug. We persist that decision in sessionStorage so language
  //      switches / hot reloads / brief remounts can never replay it.
  //   2. We NEVER overwrite an in-memory cart that already has items —
  //      otherwise an accidental re-run of this effect (after the user
  //      added items mid-session) would silently replace them with the
  //      older localStorage snapshot, which has previously been observed
  //      to look like "checkout closed and bounced me back to the menu".
  //   3. We suppress the toast entirely if the customer is already
  //      inside the Cart Review or Checkout modal at the moment the
  //      restore would fire — the toast must never coincide with payment.
  const restoreHandledRef = React.useRef(false);
  const toastShownKey = (s) => `nofee_cart_restore_shown_${s}`;
  useEffect(() => {
    if (!slug) return;
    if (restoreHandledRef.current) return;          // idempotent within this mount
    restoreHandledRef.current = true;

    const restored = loadCartFromStorage(slug);
    if (!restored || restored.length === 0) return;

    // Only hydrate state if the cart is still empty — never clobber items
    // the customer has been adding to the current session.
    setCart((current) => (current.length === 0 ? restored : current));

    // Has the toast already been shown this tab session? If yes, restore
    // silently (no toast, no modals interrupted, no scroll shift).
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(toastShownKey(slug)) === '1'; } catch (_e) { /* ignore */ }
    if (alreadyShown) return;

    // Defer one tick so the global <Toaster /> portal (App.js) is
    // guaranteed mounted before sonner flushes the queue, AND so we can
    // re-check the latest modal state via refs at fire time.
    setTimeout(() => {
      // Belt-and-suspenders: if checkout / cart modal opened during the
      // delay, skip the toast entirely. The customer is mid-flow.
      if (showCartRef.current || showCheckoutRef.current) return;
      try { sessionStorage.setItem(toastShownKey(slug), '1'); } catch (_e) { /* ignore */ }
      toast.success(t('cart.restored', 'Welcome back! We restored your cart.'), {
        duration: 5000,
        closeButton: true,
        description: t('cart.restored_subtitle', 'Items are kept for 2 hours so you can pick up where you left off.'),
      });
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Track modal openness in refs so the toast's deferred firing can
  // consult the LATEST state without re-running this effect.
  const showCartRef = React.useRef(false);
  const showCheckoutRef = React.useRef(false);
  useEffect(() => { showCartRef.current = showCart; }, [showCart]);
  useEffect(() => { showCheckoutRef.current = showCheckout; }, [showCheckout]);


  // ── Persist cart on every change ─────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    saveCartToStorage(slug, cart);
  }, [cart, slug]);

  // Compose the single `delivery_address` string from the 4 structured
  // sub-fields whenever any of them changes. This keeps the legacy
  // backend payload shape working and lets the existing eligibility
  // useEffect just keep watching `checkout.delivery_address`.
  useEffect(() => {
    if (checkout.order_type !== 'delivery') return;
    const parts = [
      checkout.delivery_street.trim(),
      checkout.delivery_city.trim(),
      [checkout.delivery_state.trim(), checkout.delivery_zip.trim()].filter(Boolean).join(' '),
    ].filter(Boolean);
    const composed = parts.join(', ');
    if (composed !== checkout.delivery_address) {
      setCheckout((c) => ({ ...c, delivery_address: composed }));
    }
  }, [
    checkout.delivery_street, checkout.delivery_city,
    checkout.delivery_state, checkout.delivery_zip,
    checkout.order_type, checkout.delivery_address,
  ]);

  // ZIP → city/state auto-fill. Runs whenever the customer finishes
  // typing 5 digits in the ZIP box. Calls the public proxy endpoint
  // we already use for signup so it benefits from the same cache.
  useEffect(() => {
    const zip = (checkout.delivery_zip || '').replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) {
      setDeliveryZipLookup({ status: 'idle', city: '', state: '', error: '' });
      return undefined;
    }
    let cancelled = false;
    setDeliveryZipLookup((p) => ({ ...p, status: 'loading' }));
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/public/zip-lookup/${zip}`);
        if (cancelled) return;
        if (r.data?.valid) {
          const city = r.data.city || '';
          const state = (r.data.state || '').toUpperCase().slice(0, 2);
          setDeliveryZipLookup({ status: 'ok', city, state, error: '' });
          setCheckout((c) => ({
            ...c,
            delivery_city: c.delivery_city ? c.delivery_city : city,
            delivery_state: c.delivery_state ? c.delivery_state : state,
          }));
        } else {
          setDeliveryZipLookup({ status: 'error', city: '', state: '', error: 'Invalid ZIP code' });
        }
      } catch {
        if (!cancelled) setDeliveryZipLookup({ status: 'error', city: '', state: '', error: 'Could not verify ZIP code' });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [checkout.delivery_zip]);

  // ── Delivery-radius eligibility (Feb 2026, iter91 strict) ───────────
  // Whenever the customer types in their delivery address, debounce 600ms
  // and call `/delivery-eligibility`. The endpoint runs in strict mode
  // for the public checkout — it returns `eligible:false` for ANY
  // blocker (no ZIP yet, invalid ZIP, ZIP↔address state mismatch,
  // un-geocodable address, OR distance > radius). We no longer wait
  // for a 5-digit ZIP locally — the server is the single source of
  // truth for "is this a valid delivery address".
  useEffect(() => {
    if (!restaurant?.id) return undefined;
    if (checkout.order_type !== 'delivery') {
      setDeliveryEligibility(null);
      return undefined;
    }
    const addr = (checkout.delivery_address || '').trim();
    if (addr.length < 5) {
      setDeliveryEligibility(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/restaurants/${restaurant.id}/delivery-eligibility`,
          { delivery_address: addr },
        );
        if (!cancelled) setDeliveryEligibility(res.data);
      } catch (err) {
        // Soft-fail: leave eligibility unset so the user isn't blocked
        // by a transient network blip. The server-side order validator
        // re-checks on submit as defense-in-depth.
        if (!cancelled) setDeliveryEligibility(null);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [checkout.delivery_address, checkout.order_type, restaurant?.id]);

  // Auto-detect / apply preferred language for this menu visit.
  // Priority: ?lang=xx URL param > user's previous MANUAL pick > restaurant.default_language > browser language
  useEffect(() => {
    const supported = SUPPORTED_LANGUAGES.map((l) => l.code);
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    let userManuallyPicked = null;
    try { userManuallyPicked = localStorage.getItem('nofee_lang_manual'); } catch (e) { /* ignore */ }

    let target = null;

    if (urlLang && supported.includes(urlLang)) {
      target = urlLang;
      try {
        localStorage.setItem('nofee_lang', urlLang);
        localStorage.setItem('nofee_lang_manual', '1');
      } catch (e) { /* ignore */ }
    } else if (userManuallyPicked) {
      // Respect user's previous manual choice — do nothing
      target = null;
    } else if (restaurant?.default_language && supported.includes(restaurant.default_language)) {
      target = restaurant.default_language;
    } else {
      // First-time visitor: use the browser language if supported
      const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
      if (supported.includes(nav)) target = nav;
    }

    if (target && target !== i18n.language) {
      i18n.changeLanguage(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id, searchParams]);

  const loadRestaurant = async () => {
    try {
      const res = await axios.get(`${API}/restaurants/${slug}`);
      setRestaurant(res.data.restaurant);
      setCategories(res.data.categories);
      setItems(res.data.items);
      if (res.data.categories.length) setActiveCategory(res.data.categories[0].id);
      // Fetch reviews + tax + today's effective hours in parallel (non-blocking)
      axios.get(`${API}/public/restaurants/${slug}/reviews`).then(r => {
        setReviews({ count: r.data.count, average: r.data.average, items: r.data.reviews || [] });
      }).catch(() => {});
      // Sales tax preview — used by the cart drawer / checkout to show
      // the customer the exact total BEFORE they pay. Driven by the
      // restaurant's address state + any owner-set local override.
      axios.get(`${API}/public/restaurants/${slug}/tax`).then(r => {
        setTaxInfo({ rate_pct: r.data.rate_pct || 0, state: r.data.state || '' });
      }).catch(() => {});
      const rid = res.data.restaurant?.id;
      if (rid) {
        axios.get(`${API}/restaurants/${rid}/hours-today`).then(r => {
          setHoursToday(r.data);
        }).catch(() => {});
      }
      // Public order-receiving channels — drives which notify options the
      // customer can pick. Failure is silent (defaults to all visible).
      axios.get(`${API}/public/restaurants/${slug}/order-channels`)
        .then((r) => setRestaurantChannels(r.data?.channels || {}))
        .catch(() => setRestaurantChannels({}));
    } catch {
      setError('Restaurant not found');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item) => {
    // Simple add (no modifiers) — legacy quick add button
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.id && (!c.modifiers || c.modifiers.length === 0));
      if (existing) return prev.map(c => c === existing ? { ...c, quantity: c.quantity + 1 } : c);
      // Append a random suffix so rapid-fire clicks in the same ms can
      // never collide on line_id (would otherwise share the same React key).
      const suffix = Math.random().toString(36).slice(2, 8);
      return [...prev, { line_id: `${item.id}-${Date.now()}-${suffix}`, item_id: item.id, name: item.name, price: item.price, quantity: 1, notes: '', modifiers: [] }];
    });
  };

  const addConfiguredItem = (configured) => {
    // configured: { id, name, price, quantity, notes, modifiers, effective_price }
    const suffix = Math.random().toString(36).slice(2, 8);
    setCart(prev => [...prev, {
      line_id: `${configured.id}-${Date.now()}-${suffix}`,
      item_id: configured.id,
      name: configured.name,
      price: configured.effective_price ?? configured.price,
      quantity: configured.quantity,
      notes: configured.notes || '',
      modifiers: configured.modifiers || [],
    }]);
  };

  const updateQty = (lineId, delta) => {
    setCart(prev => prev.map(c => c.line_id === lineId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter(c => c.quantity > 0));
  };

  // Decrement the most-recently-added line of a given item by 1.
  // Powers the "[-] N [+]" stepper rendered on each menu card.
  const decrementItemFromMenu = (itemId) => {
    setCart(prev => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].item_id === itemId) { lastIdx = i; break; }
      }
      if (lastIdx === -1) return prev;
      const next = [...prev];
      const target = next[lastIdx];
      if (target.quantity > 1) {
        next[lastIdx] = { ...target, quantity: target.quantity - 1 };
      } else {
        next.splice(lastIdx, 1);
      }
      return next;
    });
  };

  // Increment by 1. If a line for this item already exists, bump its
  // quantity directly (the customer just wants another of the same).
  // Otherwise create a brand-new plain line with no add-ons. This is
  // what gets called by every [+] tap on a menu card — the customer
  // never has to re-open the customize modal just to add another pizza.
  // To pick different extras they tap CUSTOMIZE instead.
  const incrementItemFromMenu = (item) => {
    setCart(prev => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].item_id === item.id) { lastIdx = i; break; }
      }
      if (lastIdx >= 0) {
        const next = [...prev];
        next[lastIdx] = { ...next[lastIdx], quantity: next[lastIdx].quantity + 1 };
        return next;
      }
      const suffix = Math.random().toString(36).slice(2, 8);
      return [...prev, {
        line_id: `${item.id}-${Date.now()}-${suffix}`,
        item_id: item.id,
        name: item.name,
        price: Number(item.price),
        quantity: 1,
        notes: '',
        modifiers: [],
      }];
    });
  };

  const removeLine = (lineId) => {
    setCart(prev => prev.filter(c => c.line_id !== lineId));
  };

  // Manually clear EVERYTHING — items, persisted storage, modals. Wired
  // to the "Start New Order" button (gated by a confirmation dialog so
  // a stray click can never wipe an in-progress order).
  const clearCart = () => {
    setCart([]);
    clearCartFromStorage(slug);
    // Also nuke the "restore toast already shown" flag so a *future*
    // session can legitimately surface the welcome-back toast again.
    try { sessionStorage.removeItem(`nofee_cart_restore_shown_${slug}`); } catch (_e) { /* ignore */ }
    setShowClearConfirm(false);
    setShowCart(false);
    setShowCheckout(false);
    toast.success(t('cart.cleared_toast', 'Your cart has been cleared.'), {
      duration: 4000,
      closeButton: true,
    });
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);
  const deliveryFee = checkout.order_type === 'delivery' ? (restaurant?.delivery_fee || 0) : 0;
  const tipAmount = (() => {
    if (tipPercent === null) return 0;
    if (tipPercent === 'custom') return Math.max(0, parseFloat(customTip) || 0);
    return cartTotal * (tipPercent / 100);
  })();
  // Sales tax — applied to subtotal only (not delivery, not tip).
  // Rate is fetched from /public/restaurants/:slug/tax based on the
  // restaurant's state + any owner override. Rounded to cent so the
  // displayed total matches what the backend will charge to the cent.
  const taxRatePct = Number(taxInfo?.rate_pct || 0);
  const taxAmount = Math.round(cartTotal * (taxRatePct / 100) * 100) / 100;
  const grandTotal = cartTotal + deliveryFee + taxAmount + tipAmount;

  // ── Fulfillment helpers ─────────────────────────────────────────────
  // Restaurant-controlled Pickup/Delivery availability + ETA. We treat
  // the new `pickup_enabled`/`delivery_enabled` fields as the source of
  // truth and fall back to the legacy `accepts_*` booleans so old menus
  // keep working.
  const fmt12h = (hhmm) => {
    if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return hhmm || '';
    const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };
  const isBeforeCutoff = (hhmm) => {
    if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return true; // no cutoff = always available
    const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(h, m, 0, 0);
    return now <= cutoff;
  };
  // Find the next day the restaurant is OPEN and return its opening time
  // as a friendly label. Used by the per-method "unavailable" banner so we
  // can tell customers exactly when they can come back (e.g. "Tomorrow at
  // 11:00 AM" or "Friday at 11:00 AM" when the next opening isn't tomorrow).
  const nextOpeningLabel = (businessHours) => {
    if (!businessHours || typeof businessHours !== 'object') return '';
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = new Date().getDay();
    for (let offset = 1; offset <= 7; offset += 1) {
      const idx = (today + offset) % 7;
      const h = businessHours[days[idx]];
      if (!h || h.closed === true || !h.open || !h.close) continue;
      if (h.open === h.close) continue; // legacy "closed all day"
      const when = offset === 1 ? 'tomorrow' : dayNames[idx];
      return `${when} at ${fmt12h(h.open)}`;
    }
    return '';
  };
  const fulfillment = useMemo(() => {
    const r = restaurant || {};
    const pickupEnabled = r.pickup_enabled ?? r.accepts_pickup ?? true;
    const deliveryEnabled = r.delivery_enabled ?? r.accepts_delivery ?? true;
    const pickupUntil = r.pickup_available_until || '';
    const deliveryUntil = r.delivery_available_until || '';
    return {
      pickup: {
        enabled: !!pickupEnabled,
        minutes: r.pickup_avg_minutes ?? null,
        until: pickupUntil,
        untilLabel: fmt12h(pickupUntil),
        beforeCutoff: isBeforeCutoff(pickupUntil),
        get available() { return this.enabled && this.beforeCutoff; },
      },
      delivery: {
        enabled: !!deliveryEnabled,
        minutes: r.delivery_avg_minutes ?? null,
        until: deliveryUntil,
        untilLabel: fmt12h(deliveryUntil),
        beforeCutoff: isBeforeCutoff(deliveryUntil),
        get available() { return this.enabled && this.beforeCutoff; },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.pickup_enabled, restaurant?.pickup_avg_minutes, restaurant?.pickup_available_until,
      restaurant?.delivery_enabled, restaurant?.delivery_avg_minutes, restaurant?.delivery_available_until,
      restaurant?.accepts_pickup, restaurant?.accepts_delivery]);

  // Next opening copy — used in the per-method cutoff banners so customers
  // know exactly when they can come back instead of a vague "tomorrow".
  const nextOpenLabel = useMemo(
    () => nextOpeningLabel(restaurant?.business_hours),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restaurant?.business_hours],
  );

  // Auto-switch the customer to an AVAILABLE order type if their current
  // selection is no longer available (e.g. restaurant closed delivery
  // for the night). Runs whenever fulfillment computes a new state.
  useEffect(() => {
    const current = checkout.order_type;
    if (current === 'delivery' && !fulfillment.delivery.available && fulfillment.pickup.available) {
      setCheckout((c) => ({ ...c, order_type: 'pickup' }));
    } else if (current === 'pickup' && !fulfillment.pickup.available && fulfillment.delivery.available) {
      setCheckout((c) => ({ ...c, order_type: 'delivery' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillment.pickup.available, fulfillment.delivery.available]);

  // If the restaurant has not finished Stripe Connect onboarding, the
  // Card / Apple Pay / Google Pay options must not be silently selected.
  // We automatically flip the customer to Cash on initial restaurant
  // load so the default payment method always points at something the
  // restaurant can actually accept.
  useEffect(() => {
    if (!restaurant) return;
    const cardLike = ['card', 'apple_pay', 'google_pay'];
    const ready = !!restaurant.stripe_charges_enabled && !!restaurant.stripe_account_id;
    if (!ready && cardLike.includes(checkout.payment_method)) {
      setCheckout((c) => ({ ...c, payment_method: 'cash' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id, restaurant?.stripe_charges_enabled, restaurant?.stripe_account_id]);


  // Auto-translate menu content into the customer's selected language via
  // Google Cloud Translation (backend-cached permanently in Mongo). Falls
  // back to original English silently on failure.
  const categoryNames = useMemo(() => categories.map((c) => c.name || ''), [categories]);
  const itemNames = useMemo(() => items.map((i) => i.name || ''), [items]);
  const itemDescriptions = useMemo(() => items.map((i) => i.description || ''), [items]);
  const { translated: tCategoryNames } = useTranslatedTexts(categoryNames);
  const { translated: tItemNames } = useTranslatedTexts(itemNames);
  const { translated: tItemDescriptions } = useTranslatedTexts(itemDescriptions);
  const itemNameById = useMemo(() => {
    const m = {};
    items.forEach((it, i) => { m[it.id] = tItemNames[i] || it.name; });
    return m;
  }, [items, tItemNames]);
  const itemDescById = useMemo(() => {
    const m = {};
    items.forEach((it, i) => { m[it.id] = tItemDescriptions[i] || it.description; });
    return m;
  }, [items, tItemDescriptions]);
  const categoryNameById = useMemo(() => {
    const m = {};
    categories.forEach((c, i) => { m[c.id] = tCategoryNames[i] || c.name; });
    return m;
  }, [categories, tCategoryNames]);

  // Open/closed status — uses CLIENT-SIDE business_hours computation so it
  // stays consistent with the directory card and respects the customer's
  // local timezone. We still allow `hoursToday` to TIGHTEN the gate when a
  // special-hours override marks the restaurant as closed.
  // Manual "Busy / Paused" status — owner has temporarily stopped new
  // orders. We treat it as a top-priority blocker that overrides hours
  // (per spec: Busy / Paused overrides business hours). Customers still
  // see the menu / photos / prices / reviews, but checkout is disabled.
  // If `manual_status_until` has passed, we auto-treat the restaurant as
  // Open — matches the server-side auto-resume in POST /orders.
  const isManuallyBusy = (() => {
    if ((restaurant?.manual_status || 'open') !== 'busy') return false;
    const until = restaurant?.manual_status_until;
    if (!until) return true;
    try {
      const untilMs = new Date(until).getTime();
      if (Number.isFinite(untilMs) && Date.now() >= untilMs) return false;
    } catch (_e) { /* fall through */ }
    return true;
  })();

  // Stripe Connect readiness — the canonical check used everywhere in
  // checkout to decide whether Card / Apple Pay / Google Pay can be
  // offered. We require BOTH a connected account AND `charges_enabled`
  // (the latter is webhook-synced from Stripe). Without this we silently
  // grey-out card payment up-front and surface a clear inline message —
  // the customer should NEVER click Place Order only to be told the
  // restaurant cannot accept their card.
  const stripeReady = !!restaurant?.stripe_charges_enabled && !!restaurant?.stripe_account_id;

  const openStatusForValidation = (() => {
    if (!restaurant) return { isOpen: true };
    if (isManuallyBusy) return { isOpen: false };
    const client = getOpenStatus(restaurant.business_hours);
    // Server-side special-hours close override (holiday, temporary closure,
    // custom holiday hours outside window) — let it close us, never re-open.
    if (hoursToday?.status === 'closed' && hoursToday?.special) {
      return { isOpen: false };
    }
    return client;
  })();

  // ────────────────────────────────────────────────────────────────────────
  // Checkout validation. Returns { errors: {field: msg}, firstField } and
  // never blocks silently — every reason a customer cannot check out lives
  // here, in plain language, mapped to the exact field that failed.
  // ────────────────────────────────────────────────────────────────────────
  const validateCheckout = () => {
    const errs = {};

    // Restaurant-level blockers come first — no point validating individual
    // fields if the restaurant itself can't accept the order.
    if (restaurant && restaurant.is_active === false) {
      errs._blocker = 'This restaurant is temporarily unavailable.';
    } else if (isManuallyBusy) {
      errs._blocker = 'This restaurant is currently catching up on existing orders and is temporarily not accepting new orders.';
    } else if (!openStatusForValidation.isOpen) {
      errs._blocker = 'This restaurant is currently closed.';
    } else if (checkout.order_type === 'delivery' && !fulfillment.delivery.available) {
      errs._blocker = fulfillment.delivery.enabled
        ? 'Delivery is unavailable right now. Please choose pickup or try again tomorrow.'
        : 'Delivery is currently unavailable.';
    } else if (checkout.order_type === 'pickup' && !fulfillment.pickup.available) {
      errs._blocker = fulfillment.pickup.enabled
        ? 'Pickup is unavailable right now. Please choose delivery or try again tomorrow.'
        : 'Pickup is currently unavailable.';
    }

    // Required fields by order type
    if (!checkout.customer_name.trim()) {
      errs.customer_name = 'Please enter your name.';
    }
    if (!checkout.customer_phone.trim()) {
      errs.customer_phone = 'Please enter your phone number.';
    } else if (checkout.notify_channels.includes('sms') || checkout.notify_channels.includes('whatsapp')) {
      // Validate the phone shape only when we actually need to deliver SMS/WA.
      const digits = checkout.customer_phone.replace(/\D/g, '');
      if (digits.length < 7) {
        errs.customer_phone = 'Please enter a valid phone number.';
      }
    }
    if (checkout.order_type === 'delivery') {
      // Structured address — each sub-field is required so we never
      // ship "23 Main St" to the kitchen with no idea where to drive.
      if (!checkout.delivery_street.trim()) {
        errs.delivery_street = 'Street address is required.';
      }
      if (!checkout.delivery_city.trim()) {
        errs.delivery_city = 'City is required.';
      }
      if (!checkout.delivery_state.trim() || checkout.delivery_state.trim().length !== 2) {
        errs.delivery_state = 'Enter a 2-letter state code.';
      }
      if (!/^\d{5}$/.test(checkout.delivery_zip.trim())) {
        errs.delivery_zip = 'Enter a valid 5-digit ZIP.';
      }
    }
    // Delivery-radius gate (iter91 strict): the eligibility endpoint
    // now returns `eligible:false` for every blocker (out-of-range
    // address, missing ZIP, invalid ZIP, ZIP↔address state mismatch,
    // un-geocodable address, restaurant without delivery configured).
    // Block submit on ANY such verdict — `skipped` is now reserved for
    // background/legacy callers and won't surface in this checkout
    // flow. Backend re-runs the same check as defense-in-depth.
    if (
      checkout.order_type === 'delivery' &&
      deliveryEligibility &&
      deliveryEligibility.eligible === false
    ) {
      errs.delivery_zip = deliveryEligibility.error_message
        || 'This delivery address is outside the restaurant delivery area.';
    }
    // ZIP-area check — restaurant can publish a `service_zips` whitelist. If
    // present and the typed ZIP isn't on the list, block with a friendly
    // message. Skipped entirely when restaurant didn't configure service_zips.
    if (
      checkout.order_type === 'delivery' &&
      checkout.delivery_zip.trim() &&
      Array.isArray(restaurant?.service_zips) &&
      restaurant.service_zips.length > 0
    ) {
      if (!restaurant.service_zips.includes(checkout.delivery_zip.trim())) {
        errs.delivery_zip = `We do not currently deliver to this ZIP code.`;
      }
    }
    if (!checkout.payment_method) {
      errs.payment_method = 'Please select a payment method.';
    } else if (
      ['card', 'apple_pay', 'google_pay'].includes(checkout.payment_method) &&
      !stripeReady
    ) {
      errs.payment_method =
        'Online card payments are not available for this restaurant yet. Please choose another payment method.';
    }

    // Notification-channel field validation (only matters when the channel
    // is actually selected). Email is otherwise an optional field.
    if (!checkout.notify_channels || checkout.notify_channels.length === 0) {
      errs.notify_channels = 'Please select at least one method to receive order updates.';
    }
    if (checkout.notify_channels.includes('email')) {
      const email = checkout.customer_email.trim();
      if (!email) {
        errs.customer_email = 'Please enter a valid email address.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errs.customer_email = 'Please enter a valid email address.';
      }
    }

    // Minimum order. `min_order` is dollars; we compare against `cartTotal`
    // (subtotal before delivery + tip — matches how customers think).
    if (restaurant?.min_order && cartTotal < restaurant.min_order) {
      const missing = restaurant.min_order - cartTotal;
      errs._blocker = `Minimum order amount is $${restaurant.min_order.toFixed(2)}. Add $${missing.toFixed(2)} more to place your order.`;
    }

    // First field to focus/scroll. _blocker is shown as a banner — not
    // tied to one input — so we still try to land on the first input error.
    const fieldOrder = ['customer_name', 'customer_phone', 'delivery_street', 'delivery_city', 'delivery_state', 'delivery_zip', 'customer_email', 'notify_channels', 'payment_method'];
    const firstField = fieldOrder.find((k) => errs[k]) || (errs._blocker ? '_blocker' : null);
    return { errs, firstField };
  };

  const placeOrder = async () => {
    const { errs, firstField } = validateCheckout();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Scroll the first errored field into view smoothly. `_blocker` lives
      // in a banner near the submit button — scroll the button into view in
      // that case so the customer sees the explanation.
      const targetKey = firstField || 'place-order-btn';
      const target = fieldRefs.current[targetKey];
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // For real inputs, focus them so the keyboard pops on mobile.
        if (typeof target.focus === 'function') {
          setTimeout(() => target.focus({ preventScroll: true }), 350);
        }
      }
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const cartForOrder = cart.map(c => ({
        item_id: c.item_id, name: c.name, price: c.price, quantity: c.quantity,
        notes: c.notes || '', modifiers: c.modifiers || [],
      }));
      const orderData = {
        restaurant_id: restaurant.id,
        ...checkout,
        items: cartForOrder,
        tip_amount: tipAmount,
        customer_language: (i18n.language || 'en').split('-')[0],
      };
      const orderRes = await axios.post(`${API}/orders`, orderData);
      const createdOrder = orderRes.data;

      // Card payment → Stripe Checkout (money goes directly to the restaurant)
      if (checkout.payment_method === 'card') {
        try {
          const ck = await axios.post(`${API}/checkout/order`, {
            order_id: createdOrder.id,
            origin_url: window.location.origin,
          });
          // Order is staged on the server as `awaiting_payment` — clear
          // local cart so the customer doesn't see the same items again
          // on return, and redirect to Stripe hosted checkout. The order
          // only becomes visible to the restaurant (and notifications
          // only fire) once Stripe confirms payment.
          setCart([]);
          clearCartFromStorage(slug);
          window.location.href = ck.data.checkout_url;
          return;
        } catch (payErr) {
          const detail = payErr?.response?.data?.detail || '';
          // Surface the same message we show up-front when Stripe Connect
          // isn't ready, so the customer sees one consistent explanation.
          if (/stripe|onboarding|charges_enabled|not available/i.test(detail)) {
            setErrors({
              payment_method:
                'Online card payments are not available for this restaurant yet. Please choose another payment method.',
            });
          } else {
            setErrors({ _blocker: 'Card payments are temporarily unavailable. Please pick a different payment method.' });
          }
          return;
        }
      }

      setOrderPlaced(createdOrder);
      setCart([]);
      clearCartFromStorage(slug);
      setShowCheckout(false);
      setShowCart(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrors({ _blocker: typeof detail === 'string' ? detail : t('checkout.order_failed') });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Order acceptance polling (cash orders only — Stripe-paid orders
  // get their own polling on the /payment-success page). Every 12s, the
  // confirmation screen re-fetches the order so the customer sees the
  // banner flip from "Waiting for restaurant confirmation" → "Accepted
  // by restaurant" the moment the owner clicks Accept Order in their
  // dashboard. Stops on terminal states or after 30 polls (~6 min).
  useEffect(() => {
    if (!orderPlaced?.id) return;
    if (['accepted_by_restaurant', 'rejected_by_restaurant', 'cancelled'].includes(orderPlaced.status)) return;
    let cancelled = false;
    let polls = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/orders/${orderPlaced.id}`);
        if (cancelled) return;
        setOrderPlaced((cur) => (cur ? { ...cur, ...r.data } : cur));
        const s = r.data?.status;
        if (['accepted_by_restaurant', 'rejected_by_restaurant', 'cancelled'].includes(s)) return;
        polls += 1;
        if (polls < 30) setTimeout(tick, 12000);
      } catch {
        polls += 1;
        if (polls < 30) setTimeout(tick, 12000);
      }
    };
    const t = setTimeout(tick, 12000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [orderPlaced?.id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center"><p className="text-xl text-slate-500 mb-4">{error}</p><button onClick={() => navigate('/')} className="text-gold-600 font-medium">Go to NoFee.restaurant</button></div></div>;

  // Order confirmed view
  if (orderPlaced) {
    const acceptanceStatus = orderPlaced.status || 'pending_restaurant_confirmation';
    return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center" data-testid="order-placed-view">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Order placed successfully</h2>
        <p className="text-slate-500 mb-4">Your order {orderPlaced.order_number} has been sent to {restaurant.name}</p>

        {/* Live restaurant-acceptance banner — auto-flips when the owner
            clicks Accept Order in their dashboard (poll every 12s). */}
        {acceptanceStatus === 'pending_restaurant_confirmation' && (
          <div
            data-testid="order-confirmation-banner-pending"
            className="mb-5 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-left"
          >
            <div className="flex items-center gap-2.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
              <p className="text-sm font-semibold">Waiting for restaurant confirmation</p>
            </div>
            <p className="text-xs text-amber-800/80 mt-1 pl-4">
              We&apos;ll notify you the moment the restaurant accepts your order.
            </p>
          </div>
        )}
        {acceptanceStatus === 'accepted_by_restaurant' && (
          <div
            data-testid="order-confirmation-banner-accepted"
            className="mb-5 px-4 py-3 rounded-xl border border-green-200 bg-green-50 text-green-900 text-left"
          >
            <div className="flex items-center gap-2.5">
              <span className="text-green-600 shrink-0">✓</span>
              <p className="text-sm font-semibold">Accepted by restaurant</p>
            </div>
            <p className="text-xs text-green-800/80 mt-1 pl-5">
              The restaurant received your order and will begin preparing it shortly.
            </p>
          </div>
        )}
        {acceptanceStatus === 'rejected_by_restaurant' && (
          <div
            data-testid="order-confirmation-banner-rejected"
            className="mb-5 px-4 py-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-900 text-left"
          >
            <div className="flex items-center gap-2.5">
              <span className="text-rose-600 shrink-0">✕</span>
              <p className="text-sm font-semibold">Unable to accept your order</p>
            </div>
            <p className="text-xs text-rose-800/80 mt-1 pl-5">
              Please contact the restaurant or try another option.
            </p>
          </div>
        )}

        <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
          {orderPlaced.items.map((item, i) => (
            <div key={i} className="flex justify-between text-sm py-1"><span>{item.quantity}x {item.name}</span><span>${(item.price * item.quantity).toFixed(2)}</span></div>
          ))}
          <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between font-bold"><span>Total</span><span>${orderPlaced.total.toFixed(2)}</span></div>
        </div>
        <button onClick={() => setOrderPlaced(null)} className="w-full py-3 bg-gold-500 text-white font-semibold rounded-xl hover:bg-gold-600 transition-colors">Order Again</button>
      </div>
    </div>
    );
  }

  const openStatus = restaurant ? getOpenStatus(restaurant.business_hours) : { isOpen: true, label: '' };

  // Today's regular hours, computed from the CUSTOMER'S LOCAL TIME so the
  // displayed schedule always matches the open/closed badge on this same page
  // (and the badge shown in the search results card). The server-side
  // `hoursToday` endpoint uses UTC for "today" and was producing the wrong
  // day's hours for late-evening customers in negative-UTC timezones.
  const localDayHours = (() => {
    if (!restaurant?.business_hours) return null;
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const h = restaurant.business_hours[dayKeys[new Date().getDay()]];
    if (!h || h.closed === true || !h.open || !h.close) return null;
    return { open: h.open, close: h.close };
  })();

  // Holiday Hours badge — special_hours (holiday / temporary closure / etc.)
  // ALWAYS override; otherwise use the CLIENT-SIDE open/closed flag so the
  // restaurant page stays consistent with the directory card.
  const STATUS_BADGE_LABEL = {
    open_now:           { label: 'Open Now',          cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
    closed:             { label: 'Closed',            cls: 'bg-rose-100 text-rose-700 border border-rose-200' },
    holiday_hours:      { label: 'Holiday Hours',     cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
    special_event:      { label: 'Special Event',     cls: 'bg-purple-100 text-purple-700 border border-purple-200' },
    temporary_closure:  { label: 'Temporary Closure', cls: 'bg-rose-100 text-rose-700 border border-rose-200' },
    seasonal_schedule:  { label: 'Seasonal Schedule', cls: 'bg-sky-100 text-sky-700 border border-sky-200' },
    busy_paused:        { label: 'Busy / Paused',     cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  };
  // Only let the server badge win when it represents a true SPECIAL override
  // (the regular open_now / closed values come from server UTC and were
  // disagreeing with the search-results card — defer to client for those).
  const SPECIAL_BADGES = new Set(['holiday_hours', 'special_event', 'temporary_closure', 'seasonal_schedule']);
  const serverBadge = hoursToday?.badge;
  // Manual Busy / Paused takes precedence over everything (per spec: it
  // overrides business hours).
  const badgeKey = isManuallyBusy
    ? 'busy_paused'
    : (serverBadge && SPECIAL_BADGES.has(serverBadge))
      ? serverBadge
      : (openStatus.isOpen ? 'open_now' : 'closed');
  const badgeMeta = STATUS_BADGE_LABEL[badgeKey] || STATUS_BADGE_LABEL.open_now;

  // SEO — derived from restaurant data; first 160 chars of description used for meta
  const seoTitle = `${restaurant.name} — Order Online${restaurant.cuisine_type ? ` · ${restaurant.cuisine_type}` : ''}`;
  const seoDescription = (() => {
    const tagline = restaurant.description?.trim();
    if (tagline) return tagline.slice(0, 155).replace(/\s+\S*$/, '') + (tagline.length > 155 ? '…' : '');
    return `Order directly from ${restaurant.name} — 0% commission, ${restaurant.accepts_delivery !== false ? 'delivery' : 'pickup'} available. Pay with Stripe on NoFee.`;
  })();
  const seoImage = restaurant.cover_url || restaurant.logo_url || undefined;

  // JSON-LD Restaurant schema — drives Rich Results (rating stars, cuisine, address)
  const restaurantJsonLd = (() => {
    const ld = {
      "@type": "Restaurant",
      "@id": `https://nofee.restaurant/menu/${slug}`,
      "name": restaurant.name,
      "url": `https://nofee.restaurant/menu/${slug}`,
      "image": seoImage,
      "priceRange": "$$",
      "acceptsReservations": false,
    };
    if (restaurant.cuisine_type) ld.servesCuisine = restaurant.cuisine_type;
    if (restaurant.address) {
      // Address may be either a freeform string or a structured
      // {street, city, state, zip} object — render-friendly + JSON-LD
      // both prefer the formatted single-line string.
      const addrStr = typeof restaurant.address === 'string'
        ? restaurant.address
        : [restaurant.address.street, restaurant.address.city, restaurant.address.state, restaurant.address.zip].filter(Boolean).join(', ');
      if (addrStr) ld.address = { "@type": "PostalAddress", "streetAddress": addrStr };
    }
    if (restaurant.phone) ld.telephone = restaurant.phone;
    if (reviews.count > 0) {
      ld.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": Number(reviews.average.toFixed(1)),
        "reviewCount": reviews.count,
        "bestRating": 5,
        "worstRating": 1,
      };
    }
    // Wrap in @graph alongside a BreadcrumbList so Google renders breadcrumb
    // path on the SERP (Home › Restaurants › <restaurant>).
    return {
      "@context": "https://schema.org",
      "@graph": [
        ld,
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nofee.restaurant/" },
            { "@type": "ListItem", "position": 2, "name": "Restaurants", "item": "https://nofee.restaurant/restaurants" },
            { "@type": "ListItem", "position": 3, "name": restaurant.name, "item": `https://nofee.restaurant/menu/${slug}` },
          ],
        },
      ],
    };
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/menu/${slug}`}
        image={seoImage}
        type="restaurant.menu"
        jsonLd={restaurantJsonLd}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          {/* Logo + restaurant identity. Logo is uploaded by the
              owner in the dashboard and is distinct from the cover
              image (cover lives on directory / hero surfaces). We
              render through FoodImage so it gets responsive
              srcSet + lazy-load via Cloudinary. When no logo is
              uploaded we fall back to a clean, neutral monogram
              avatar so the layout doesn't collapse. */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 ring-1 ring-slate-100"
              data-testid="public-menu-restaurant-logo"
            >
              {restaurant.logo_url ? (
                <FoodImage
                  src={restaurant.logo_url}
                  alt={`${restaurant.name} logo`}
                  variant="thumb"
                  className="w-full h-full"
                  rounded="rounded-full"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-slate-500 font-bold text-base bg-gradient-to-br from-slate-100 to-slate-200"
                  data-testid="public-menu-logo-placeholder"
                  aria-hidden="true"
                >
                  {(restaurant.name || '?').trim().charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-slate-900">{restaurant.name}</h1>
              <FavoriteHeartButton
                restaurantId={restaurant.id}
                restaurantName={restaurant.name}
                variant="inline"
              />
              <span
                data-testid="public-menu-status-badge"
                data-badge={badgeKey}
                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full ${badgeMeta.cls}`}
              >
                {badgeMeta.label}
              </span>
              {hoursToday?.special?.title && (
                <span
                  data-testid="public-menu-special-title"
                  className="text-[10px] text-amber-700 font-semibold"
                >
                  · {hoursToday.special.title}
                </span>
              )}
            </div>
            {(() => {
              // Prefer special-hours times when an override is active;
              // otherwise show the customer's local-day regular hours.
              const showSpecial = hoursToday?.special && hoursToday?.today_open && hoursToday?.today_close;
              const openLbl = showSpecial ? hoursToday.today_open : localDayHours?.open;
              const closeLbl = showSpecial ? hoursToday.today_close : localDayHours?.close;
              if (!openLbl || !closeLbl) return null;
              return (
                <p data-testid="public-menu-todays-hours" className="text-[11px] text-slate-500 mt-0.5">
                  Today: {openLbl} – {closeLbl}
                  {hoursToday?.special?.note && <span className="ml-1.5 italic text-amber-700">· {hoursToday.special.note}</span>}
                </p>
              );
            })()}
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
              {restaurant.address && (
                <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{
                  typeof restaurant.address === 'string'
                    ? restaurant.address
                    : [restaurant.address.street, restaurant.address.city, restaurant.address.state, restaurant.address.zip].filter(Boolean).join(', ')
                }</span>
              )}
              {reviews.count > 0 && (
                <button
                  onClick={() => setShowReviews(true)}
                  className="flex items-center gap-1 text-slate-700 font-semibold"
                  data-testid="rating-summary"
                >
                  <Star className="w-3.5 h-3.5 fill-gold-500 text-gold-500" />
                  {reviews.average.toFixed(1)} <span className="text-slate-400 font-normal">({reviews.count})</span>
                </button>
              )}
            </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 shrink-0 ml-3">
            <LanguageSwitcher variant="light" compact />
            {cartCount > 0 && (
              <button
                type="button"
                onClick={() => setShowCart(true)}
                data-testid="public-menu-cart-icon"
                aria-label={t('cart.view_cart', 'View Cart')}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-gold-500 text-white hover:bg-gold-600 active:scale-95 transition-all shadow-md"
              >
                <ShoppingCart className="w-4 h-4" />
                <span
                  data-testid="public-menu-cart-icon-count"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white tabular-nums"
                >
                  {cartCount}
                </span>
              </button>
            )}
            {/* Dynamic ETA — driven by Order Fulfillment Settings ONLY.
                Shows each AVAILABLE method's average time (e.g. "Pickup
                20 min · Delivery 45 min"). We no longer render the
                legacy `estimated_time` default string — if neither
                method has avg_minutes set, no ETA is shown. */}
            {(() => {
              const parts = [];
              if (fulfillment.pickup.available && fulfillment.pickup.minutes) {
                parts.push({ key: 'pickup', Icon: Store, label: `${t('menu.pickup')} ${fulfillment.pickup.minutes} ${t('fulfillment.min', 'min')}` });
              }
              if (fulfillment.delivery.available && fulfillment.delivery.minutes) {
                parts.push({ key: 'delivery', Icon: Truck, label: `${t('menu.delivery')} ${fulfillment.delivery.minutes} ${t('fulfillment.min', 'min')}` });
              }
              if (parts.length === 0) {
                return null;
              }
              return (
                <span className="flex items-center gap-2" data-testid="header-eta">
                  {parts.map((p, i) => (
                    <React.Fragment key={p.key}>
                      {i > 0 && <span className="text-slate-300" aria-hidden="true">·</span>}
                      <span
                        className="flex items-center gap-1"
                        data-testid={`header-eta-${p.key}`}
                      >
                        <p.Icon className="w-3.5 h-3.5" /> {p.label}
                      </span>
                    </React.Fragment>
                  ))}
                </span>
              );
            })()}
          </div>
        </div>
        {/* Category tabs */}
        <div className="max-w-3xl mx-auto px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat.id ? 'bg-gold-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}>
              {categoryNameById[cat.id] || cat.name}
            </button>
          ))}
        </div>
      </header>

      {/* Fast by NoFee reputation banner — removed from the customer
          menu (iter135, Jul 2026). Owner spec: menu must stay clean
          and focused on ordering. Fast by NoFee still lives in the
          Restaurant Benefits page (`/for-restaurants`), the owner
          dashboard, and the post-delivery confirmation flow — but
          NEVER above the menu categories. */}

      {/* iter91 (Feb 2026): the pre-order "Got a question? Reach us
          directly" ContactChannelsBar was removed from the public menu
          page so customers stay inside the NoFee checkout flow instead
          of contacting the restaurant out-of-band before placing an
          order. The `restaurantChannels` state is still loaded and used
          at checkout to drive the notify-channel options. */}

      {/* Closed / Busy banner — menu stays fully visible so customers can
          browse categories, products, prices and add-ons. Only ordering
          actions are blocked at checkout. When the owner has manually
          paused, we surface the dedicated Busy / Temporarily Paused copy
          per spec. */}
      {isManuallyBusy ? (
        <div
          data-testid="public-menu-busy-banner"
          className="max-w-3xl mx-auto px-4 pt-4"
        >
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
            <p className="leading-snug">
              <span className="font-semibold">Busy / Temporarily Paused.</span>{' '}
              This restaurant is currently catching up on existing orders and is temporarily not accepting new orders.
            </p>
          </div>
        </div>
      ) : !openStatusForValidation.isOpen && (
        <div
          data-testid="public-menu-closed-banner"
          className="max-w-3xl mx-auto px-4 pt-4"
        >
          <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-rose-500" />
            <p className="leading-snug">
              <span className="font-semibold">Restaurant is currently closed.</span>{' '}
              You can browse the menu and place orders during business hours.
            </p>
          </div>
        </div>
      )}

      {/* Menu items */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-24">
        {categories.filter(c => c.id === activeCategory || !activeCategory).map(cat => (
          <div key={cat.id} className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-3">{categoryNameById[cat.id] || cat.name}</h2>
            <div className="grid gap-3">
              {items.filter(i => i.category_id === cat.id && i.is_available).map(item => {
                const lineCount = cart.filter(c => c.item_id === item.id).reduce((s,c) => s+c.quantity, 0);
                const hasModifiers = (item.modifier_groups || []).length > 0;
                const addonCount = (item.modifier_groups || []).reduce((s, g) => s + (g.options || []).length, 0);
                // ALL items open the detail modal so the customer can
                // always leave kitchen notes (e.g. "no onions", "well
                // done") even when the item has no add-ons configured.
                const handleClick = () => setDetailItem(item);
                const stop = (e) => { e.stopPropagation(); };
                return (
                  <div
                    key={item.id}
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
                    className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 flex gap-4 hover:border-gold-300 hover:shadow-sm transition-all cursor-pointer"
                    data-testid={`menu-item-${item.id}`}
                  >
                    {/* Menu card thumbnail — small square thumb on the
                        left (per product spec: horizontal card layout).
                        FoodImage handles lazy loading, srcSet, and the
                        no-photo fallback. */}
                    <FoodImage
                      src={item.image_url}
                      alt={item.name}
                      variant="thumb"
                      aspectClass="w-24 h-24 shrink-0"
                      rounded="rounded-lg"
                      testid={`menu-item-${item.id}-image`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{itemNameById[item.id] || item.name}</div>
                      {item.description && <div className="text-sm text-slate-500 mt-0.5 line-clamp-2">{itemDescById[item.id] || item.description}</div>}
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <div className="text-gold-600 font-bold">${item.price.toFixed(2)}</div>
                        {/* CUSTOMIZE pill shows on 100% of items so every
                            customer can always leave kitchen notes,
                            even when no add-ons are configured. */}
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.14em] ${
                            hasModifiers
                              ? 'bg-gold-50 border border-gold-300 text-gold-700'
                              : 'bg-white border border-slate-300 text-slate-600'
                          }`}
                          data-testid={`menu-item-${item.id}-customize-badge`}
                        >
                          <Plus className="w-2.5 h-2.5" />
                          {hasModifiers ? `Customize · +${addonCount} ${addonCount === 1 ? 'option' : 'options'}` : 'Customize'}
                        </span>
                      </div>
                    </div>
                    <div className="self-end" onClick={stop}>
                      {lineCount > 0 ? (
                        // In cart → clear [−] N [+] stepper. Tapping [+]
                        // just adds another of the same item (NO modal).
                        // To customise add-ons the customer taps CUSTOMIZE.
                        <div
                          className="inline-flex items-center gap-2 bg-gold-500 text-white rounded-full pl-1 pr-1 py-1 shadow-md"
                          data-testid={`menu-item-${item.id}-stepper`}
                        >
                          <button
                            type="button"
                            onClick={(e) => { stop(e); decrementItemFromMenu(item.id); }}
                            data-testid={`menu-item-${item.id}-minus`}
                            aria-label="Remove one"
                            className="w-7 h-7 rounded-full hover:bg-white/15 active:scale-95 flex items-center justify-center transition-all"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span
                            className="min-w-[1.25rem] text-center font-bold text-sm"
                            data-testid={`menu-item-${item.id}-qty-badge`}
                          >
                            {lineCount}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { stop(e); incrementItemFromMenu(item); }}
                            data-testid={`menu-item-${item.id}-plus`}
                            aria-label="Add another"
                            className="w-7 h-7 rounded-full hover:bg-white/15 active:scale-95 flex items-center justify-center transition-all"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        // Not in cart → single circular [+].
                        // If the item has NO add-ons configured, tapping [+]
                        // drops it straight into the cart (no modal needed).
                        // If it DOES have add-ons, we open the customize
                        // modal so the customer can pick the extras first.
                        <button
                          type="button"
                          onClick={(e) => {
                            stop(e);
                            if (hasModifiers) handleClick();
                            else incrementItemFromMenu(item);
                          }}
                          data-testid={`menu-item-${item.id}-add`}
                          aria-label={t('cart.add', 'Add')}
                          className="w-9 h-9 bg-gold-50 text-gold-600 rounded-full flex items-center justify-center hover:bg-gold-100 active:scale-95 transition-all"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </main>

      {/* Cart bar — opens the Cart Review modal (NOT directly checkout).
          Customer must explicitly tap "Checkout" inside the review. */}
      {cartCount > 0 && !showCheckout && !showCart && (
        <div
          data-testid="public-menu-cart-bar"
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-20 shadow-[0_-12px_30px_-12px_rgba(0,0,0,0.18)]"
        >
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => setShowCart(true)}
              data-testid="cart-bar-view-cart"
              className="w-full py-4 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-2xl flex items-center justify-between px-6 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                <span>{t('cart.view_cart', 'View Cart')}</span>
                <span className="px-2 py-0.5 rounded-full bg-white/25 text-[11px] font-bold">
                  {cartCount}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span>${cartTotal.toFixed(2)}</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ─── Cart Review modal ──────────────────────────────────────────
          A dedicated cart screen between the floating bar and the
          checkout form. Customers can review, edit quantities, remove
          items, see all fees + tip, then decide to either continue
          shopping (close, keep cart) or proceed to checkout. */}
      {showCart && !showCheckout && (
        <div
          data-testid="cart-review-modal"
          className="fixed inset-0 bg-black/50 z-30 flex items-end sm:items-center justify-center"
        >
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  {t('cart.title', 'Your cart')}
                </h3>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  {restaurant?.name} · {cartCount} {cartCount === 1 ? t('cart.item', 'item') : t('cart.items', 'items')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {cart.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowClearConfirm(true)}
                    data-testid="cart-start-new-order"
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-500 hover:text-rose-500 transition-colors px-2 py-1.5 rounded-lg"
                  >
                    {t('cart.start_new_order', 'Start New Order')}
                  </button>
                )}
                <button
                  onClick={() => setShowCart(false)}
                  data-testid="cart-review-close"
                  className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                  aria-label={t('common.close', 'Close')}
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            {/* Scrollable items list */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-3" data-testid="cart-review-items">
              {cart.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">
                  {t('cart.empty', 'Your cart is empty. Browse the menu to add items.')}
                </div>
              ) : cart.map((c) => {
                // Resolve item to get the photo. Falls back to a placeholder
                // if the menu item is no longer available (e.g. removed by
                // the restaurant while the cart was sitting in localStorage).
                const linkedItem = items.find(it => it.id === c.item_id);
                const photo = linkedItem?.image_url;
                return (
                <div
                  key={c.line_id}
                  data-testid={`cart-line-${c.line_id}`}
                  className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
                >
                  <FoodImage
                    src={photo}
                    alt={c.name}
                    variant="thumb"
                    aspectClass="w-16 h-16 shrink-0"
                    rounded="rounded-lg"
                    testid={`cart-line-${c.line_id}-photo`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-bold text-slate-900 leading-tight">{c.name}</p>
                    <p className="text-[12px] text-slate-500 mt-0.5">
                      ${c.price.toFixed(2)} {t('cart.each', 'each')}
                    </p>
                    {c.modifiers?.length > 0 && (
                      <p className="text-[12px] text-slate-500 mt-1 leading-snug">
                        {c.modifiers.map((m) => {
                          const q = Number(m.quantity || 1);
                          const unit = Number(m.price_delta || 0);
                          const label = q > 1 ? `${q}× ${m.option}` : m.option;
                          if (unit > 0) {
                            return `${label} (+$${(unit * q).toFixed(2)})`;
                          }
                          return label;
                        }).join(' · ')}
                      </p>
                    )}
                    {c.notes && (
                      <p className="text-[11.5px] text-slate-400 italic mt-1">&ldquo;{c.notes}&rdquo;</p>
                    )}
                    <div className="flex items-center gap-3 mt-2.5">
                      {/* qty stepper */}
                      <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full border border-slate-200">
                        <button
                          onClick={() => updateQty(c.line_id, -1)}
                          data-testid={`cart-line-${c.line_id}-decrease`}
                          className="w-6 h-6 rounded-full hover:bg-slate-100 text-slate-700 flex items-center justify-center"
                          aria-label={t('cart.decrease', 'Decrease quantity')}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-5 text-center text-[13px] font-bold text-slate-900" data-testid={`cart-line-${c.line_id}-qty`}>
                          {c.quantity}
                        </span>
                        <button
                          onClick={() => updateQty(c.line_id, 1)}
                          data-testid={`cart-line-${c.line_id}-increase`}
                          className="w-6 h-6 rounded-full bg-gold-500 hover:bg-gold-600 text-white flex items-center justify-center"
                          aria-label={t('cart.increase', 'Increase quantity')}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeLine(c.line_id)}
                        data-testid={`cart-line-${c.line_id}-remove`}
                        className="text-[11.5px] font-semibold text-rose-500 hover:text-rose-600 transition-colors"
                      >
                        {t('cart.remove', 'Remove')}
                      </button>
                    </div>
                  </div>
                  <span className="text-[14px] font-bold text-slate-900 shrink-0">
                    ${(c.price * c.quantity).toFixed(2)}
                  </span>
                </div>
                );
              })}
            </div>

            {/* Totals + actions */}
            {cart.length > 0 && (
              <div className="border-t border-slate-100 px-5 sm:px-6 py-4 space-y-3 bg-slate-50/50">
                <div className="space-y-1 text-[13.5px]">
                  <div className="flex justify-between text-slate-600">
                    <span>{t('checkout.subtotal', 'Subtotal')}</span>
                    <span className="font-semibold text-slate-900">${cartTotal.toFixed(2)}</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>{t('checkout.delivery', 'Delivery')}</span>
                      <span className="font-semibold text-slate-900">${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  {/* Sales Tax — driven by the restaurant's state +
                      owner override. Hidden in zero-tax states/restaurants
                      (e.g. OR, NH, no address set) so the line doesn't
                      appear as "$0.00" noise. */}
                  {taxAmount > 0 && (
                    <div className="flex justify-between text-slate-600" data-testid="checkout-sales-tax">
                      <span>
                        {t('checkout.sales_tax', 'Sales Tax')}
                        {taxRatePct > 0 ? ` (${taxRatePct.toFixed(2)}%)` : ''}
                      </span>
                      <span className="font-semibold text-slate-900">${taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {tipAmount > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>
                        {t('checkout.tip', 'Tip')}
                        {tipPercent !== 'custom' && tipPercent !== null ? ` (${tipPercent}%)` : ''}
                      </span>
                      <span className="font-semibold text-slate-900">${tipAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[16px] font-extrabold pt-2 border-t border-slate-200">
                    <span>{t('checkout.total', 'Total')}</span>
                    <span className="text-gold-600">${grandTotal.toFixed(2)}</span>
                  </div>
                  {!deliveryFee && !tipAmount && (
                    <p className="text-[11px] text-slate-400 italic pt-1">
                      {t('cart.fees_at_checkout', 'Delivery and tip are added at checkout.')}
                    </p>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
                  <button
                    onClick={() => setShowCart(false)}
                    data-testid="cart-continue-shopping"
                    className="flex-1 py-3 rounded-xl border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-bold text-[13.5px] transition-colors"
                  >
                    {t('cart.continue_shopping', 'Continue shopping')}
                  </button>
                  <button
                    onClick={() => {
                      setShowCart(false);
                      setShowCheckout(true);
                    }}
                    data-testid="cart-go-to-checkout"
                    className="flex-1 py-3 rounded-xl bg-gold-500 hover:bg-gold-600 text-white font-bold text-[13.5px] transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {t('cart.checkout', 'Checkout')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Start-New-Order confirmation overlay ────────────────────
          Renders ABOVE the cart review modal (z-40 > the modal's z-30)
          so the customer sees their items behind the dimmed backdrop
          and understands exactly what's about to be cleared. */}
      {showClearConfirm && (
        <div
          data-testid="cart-clear-confirm-modal"
          className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center px-4"
          onClick={() => setShowClearConfirm(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900">
              {t('cart.confirm_clear_title', 'Start a new order?')}
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              {t('cart.confirm_clear_body', 'Are you sure you want to clear your cart and start a new order?')}
            </p>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                data-testid="cart-clear-cancel"
                className="flex-1 py-3 rounded-xl border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-bold text-[13.5px] transition-colors"
              >
                {t('cart.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={clearCart}
                data-testid="cart-clear-confirm"
                className="flex-1 py-3 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-[13.5px] transition-colors"
              >
                {t('cart.confirm_clear_button', 'Clear Cart')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 z-30 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowCheckout(false); setShowCart(true); }}
                  data-testid="checkout-back-to-cart"
                  className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                  aria-label={t('cart.back_to_cart', 'Back to cart')}
                  title={t('cart.back_to_cart', 'Back to cart')}
                >
                  <ArrowLeft className="w-5 h-5 text-slate-500" />
                </button>
                <h3 className="text-xl font-bold">{t('checkout.title', 'Checkout')}</h3>
              </div>
              <button onClick={() => setShowCheckout(false)} data-testid="checkout-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            {/* Cart summary */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              {cart.map(c => (
                <div key={c.line_id} className="flex items-start justify-between py-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex items-center gap-1 mt-0.5">
                      <button onClick={() => updateQty(c.line_id, -1)} className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center text-sm font-bold">{c.quantity}</span>
                      <button onClick={() => updateQty(c.line_id, 1)} className="w-6 h-6 rounded bg-gold-500 text-white flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm leading-tight">{c.name}</p>
                      {c.modifiers?.length > 0 && (
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {c.modifiers.map((m) => {
                            const q = Number(m.quantity || 1);
                            const unit = Number(m.price_delta || 0);
                            const label = q > 1 ? `${q}× ${m.option}` : m.option;
                            return unit > 0 ? `${label} (+$${(unit * q).toFixed(2)})` : label;
                          }).join(' · ')}
                        </p>
                      )}
                      {c.notes && <p className="text-[11px] text-slate-400 mt-0.5 italic">"{c.notes}"</p>}
                    </div>
                  </div>
                  <span className="text-sm font-medium ml-2 shrink-0">${(c.price * c.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 mt-2 pt-2 space-y-1">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>${cartTotal.toFixed(2)}</span></div>
                {deliveryFee > 0 && <div className="flex justify-between text-sm"><span>Delivery</span><span>${deliveryFee.toFixed(2)}</span></div>}
                {taxAmount > 0 && <div className="flex justify-between text-sm" data-testid="checkout-sales-tax-2"><span>Sales Tax{taxRatePct > 0 ? ` (${taxRatePct.toFixed(2)}%)` : ''}</span><span>${taxAmount.toFixed(2)}</span></div>}
                {tipAmount > 0 && <div className="flex justify-between text-sm"><span>Tip{tipPercent !== 'custom' && tipPercent !== null ? ` (${tipPercent}%)` : ''}</span><span>${tipAmount.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="text-gold-600">${grandTotal.toFixed(2)}</span></div>
              </div>
            </div>

            {/* Tip selector */}
            <div className="mb-5">
              <p className="text-sm font-medium text-slate-700 mb-1">Add a tip <span className="text-slate-400 font-normal">— 100% goes to the staff</span></p>
              <div className="grid grid-cols-5 gap-1.5" data-testid="tip-selector">
                {[15, 18, 20, 25].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setTipPercent(tipPercent === p ? null : p)}
                    data-testid={`tip-${p}`}
                    className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                      tipPercent === p
                        ? 'bg-gold-500 text-white border-gold-500'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-gold-300'
                    }`}
                  >
                    {p}%
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setTipPercent(tipPercent === 'custom' ? null : 'custom')}
                  data-testid="tip-custom"
                  className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                    tipPercent === 'custom'
                      ? 'bg-gold-500 text-white border-gold-500'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-gold-300'
                  }`}
                >
                  Custom
                </button>
              </div>
              {tipPercent === 'custom' && (
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={customTip}
                    onChange={(e) => setCustomTip(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2 rounded-lg border-2 border-gold-300 text-sm focus:border-gold-500 outline-none"
                    data-testid="tip-custom-input"
                  />
                </div>
              )}
              {tipPercent !== null && (
                <button
                  type="button"
                  onClick={() => { setTipPercent(null); setCustomTip(''); }}
                  className="text-xs text-slate-400 mt-2 underline hover:text-slate-600"
                >
                  Remove tip
                </button>
              )}
            </div>

            {/* Order type — driven by Order Fulfillment Settings.
                Hidden options when the restaurant has disabled them.
                Disabled (greyed out) when past the daily cutoff time. */}
            <div className="space-y-2 mb-5" data-testid="checkout-order-type">
              <div className="flex gap-2">
                {fulfillment.delivery.enabled && (
                  <button
                    type="button"
                    onClick={() => fulfillment.delivery.beforeCutoff && setCheckout({...checkout, order_type: 'delivery'})}
                    disabled={!fulfillment.delivery.beforeCutoff}
                    data-testid="checkout-order-type-delivery"
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-colors ${
                      !fulfillment.delivery.beforeCutoff
                        ? 'bg-slate-100 text-slate-300 cursor-not-allowed line-through'
                        : checkout.order_type === 'delivery'
                          ? 'bg-gold-500 text-white'
                          : 'bg-slate-100 text-slate-600'
                    }`}>
                    <Truck className="w-4 h-4" /> {t('menu.delivery')}
                  </button>
                )}
                {fulfillment.pickup.enabled && (
                  <button
                    type="button"
                    onClick={() => fulfillment.pickup.beforeCutoff && setCheckout({...checkout, order_type: 'pickup'})}
                    disabled={!fulfillment.pickup.beforeCutoff}
                    data-testid="checkout-order-type-pickup"
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-colors ${
                      !fulfillment.pickup.beforeCutoff
                        ? 'bg-slate-100 text-slate-300 cursor-not-allowed line-through'
                        : checkout.order_type === 'pickup'
                          ? 'bg-gold-500 text-white'
                          : 'bg-slate-100 text-slate-600'
                    }`}>
                    <Store className="w-4 h-4" /> {t('menu.pickup')}
                  </button>
                )}
              </div>

              {/* Per-method cutoff banners — only render while the restaurant
                  is currently OPEN. When the restaurant is fully closed, the
                  broader "closed" message handles it; rendering this banner
                  on top of that just produced two contradictory signals
                  ("OPEN NOW" badge vs "Available tomorrow") and disabled
                  both order types — a major conversion blocker. While open,
                  we surface a soft amber notice with the exact last-call
                  time and the next opening so the customer knows when to
                  come back. */}
              {openStatusForValidation.isOpen && fulfillment.pickup.enabled && !fulfillment.pickup.beforeCutoff && (
                <div
                  data-testid="checkout-pickup-unavailable"
                  className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm"
                >
                  <p className="font-semibold text-amber-800">
                    {fulfillment.pickup.untilLabel
                      ? t('fulfillment.pickup_last_call', {
                          defaultValue: 'Pickup orders ended at {{time}}',
                          time: fulfillment.pickup.untilLabel,
                        })
                      : t('fulfillment.pickup_unavailable', 'Pickup unavailable')}
                  </p>
                  <p className="text-amber-700 text-xs mt-0.5">
                    {nextOpenLabel
                      ? t('fulfillment.pickup_resumes', {
                          defaultValue: 'Pickup resumes {{when}}.',
                          when: nextOpenLabel,
                        })
                      : t('fulfillment.available_tomorrow', 'Available tomorrow at opening time')}
                  </p>
                </div>
              )}
              {openStatusForValidation.isOpen && fulfillment.delivery.enabled && !fulfillment.delivery.beforeCutoff && (
                <div
                  data-testid="checkout-delivery-unavailable"
                  className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm"
                >
                  <p className="font-semibold text-amber-800">
                    {fulfillment.delivery.untilLabel
                      ? t('fulfillment.delivery_last_call', {
                          defaultValue: 'Delivery orders ended at {{time}}',
                          time: fulfillment.delivery.untilLabel,
                        })
                      : t('fulfillment.delivery_unavailable', 'Delivery unavailable')}
                  </p>
                  <p className="text-amber-700 text-xs mt-0.5">
                    {nextOpenLabel
                      ? t('fulfillment.delivery_resumes', {
                          defaultValue: 'Delivery resumes {{when}}.',
                          when: nextOpenLabel,
                        })
                      : t('fulfillment.available_tomorrow', 'Available tomorrow at opening time')}
                  </p>
                </div>
              )}

              {/* Active fulfillment ETA card — only when the currently
                  selected method IS available. The unavailable banners
                  above already cover the past-cutoff state. */}
              {(() => {
                const active = checkout.order_type === 'pickup' ? fulfillment.pickup : fulfillment.delivery;
                if (!active.enabled || !active.beforeCutoff) return null;
                return (
                  <div
                    data-testid={`checkout-${checkout.order_type}-eta`}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
                  >
                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                      {checkout.order_type === 'pickup'
                        ? <><Store className="w-4 h-4 text-gold-600" /> {t('menu.pickup')}</>
                        : <><Truck className="w-4 h-4 text-gold-600" /> {t('menu.delivery')}</>}
                    </div>
                    {active.minutes ? (
                      <p className="text-slate-700 text-xs mt-1">
                        {checkout.order_type === 'pickup'
                          ? t('fulfillment.ready_in', { defaultValue: 'Ready in approximately {{minutes}} minutes', minutes: active.minutes })
                          : t('fulfillment.estimated_delivery', { defaultValue: 'Estimated delivery: {{minutes}} minutes', minutes: active.minutes })}
                      </p>
                    ) : null}
                    {active.untilLabel ? (
                      <p className="text-slate-500 text-xs mt-0.5">
                        {t('fulfillment.available_until', { defaultValue: 'Available until {{time}}', time: active.untilLabel })}
                      </p>
                    ) : null}
                  </div>
                );
              })()}
            </div>

            {/* Customer info */}
            <div className="space-y-3 mb-5" data-testid="checkout-fields">
              <FieldWithError
                error={errors.customer_name}
                inputRef={(el) => (fieldRefs.current.customer_name = el)}
              >
                <input
                  value={checkout.customer_name}
                  onChange={(e) => {
                    setCheckout({ ...checkout, customer_name: e.target.value });
                    if (errors.customer_name) setErrors({ ...errors, customer_name: undefined });
                  }}
                  data-testid="checkout-name"
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-colors ${errors.customer_name ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'}`}
                  placeholder={t('checkout.your_name')}
                />
              </FieldWithError>

              <FieldWithError
                error={errors.customer_phone}
                inputRef={(el) => (fieldRefs.current.customer_phone = el)}
              >
                <input
                  value={checkout.customer_phone}
                  onChange={(e) => {
                    setCheckout({ ...checkout, customer_phone: e.target.value });
                    if (errors.customer_phone) setErrors({ ...errors, customer_phone: undefined });
                  }}
                  data-testid="checkout-phone"
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-colors ${errors.customer_phone ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'}`}
                  placeholder={t('checkout.phone_placeholder')}
                />
              </FieldWithError>

              <FieldWithError
                error={errors.customer_email}
                inputRef={(el) => (fieldRefs.current.customer_email = el)}
              >
                <input
                  value={checkout.customer_email}
                  onChange={(e) => {
                    setCheckout({ ...checkout, customer_email: e.target.value });
                    if (errors.customer_email) setErrors({ ...errors, customer_email: undefined });
                  }}
                  data-testid="checkout-email"
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-colors ${errors.customer_email ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'}`}
                  placeholder={t('checkout.email_optional')}
                />
              </FieldWithError>

              {checkout.order_type === 'delivery' && (
                <div className="space-y-3" data-testid="checkout-delivery-fields">
                  {usingSavedAddress && savedAddresses.length > 0 && (
                    <div
                      className="px-4 py-3 rounded-xl border border-emerald-300 bg-emerald-50 flex items-center justify-between gap-3"
                      data-testid="checkout-saved-address-pill"
                    >
                      <div className="min-w-0">
                        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                          Delivering to your saved address
                        </p>
                        <p className="text-[13px] font-semibold text-emerald-900 mt-0.5 truncate">
                          {(() => {
                            const a = savedAddresses.find((x) => x.id === activeSavedAddrId) || savedAddresses[0];
                            return a ? `${a.label ? a.label + ' · ' : ''}${a.address}, ${a.city || ''} ${a.state || ''} ${a.zip_code || ''}`.replace(/\s+,/g, ',') : '';
                          })()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {savedAddresses.length > 1 && (
                          <select
                            value={activeSavedAddrId || ''}
                            onChange={(e) => {
                              const a = savedAddresses.find((x) => x.id === e.target.value);
                              if (!a) return;
                              setActiveSavedAddrId(a.id);
                              setCheckout((c) => ({
                                ...c,
                                delivery_street: a.address || '',
                                delivery_city:   a.city    || '',
                                delivery_state:  (a.state || '').toUpperCase(),
                                delivery_zip:    a.zip_code || '',
                              }));
                            }}
                            className="text-[12px] font-semibold rounded-lg border border-emerald-300 bg-white text-emerald-900 px-2 py-1"
                            data-testid="checkout-saved-address-select"
                          >
                            {savedAddresses.map((a) => (
                              <option key={a.id} value={a.id}>{a.label || 'Address'}</option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setUsingSavedAddress(false);
                            setActiveSavedAddrId(null);
                            setCheckout((c) => ({
                              ...c,
                              delivery_street: '', delivery_city: '', delivery_state: '', delivery_zip: '',
                            }));
                          }}
                          className="text-[12px] font-bold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                          data-testid="checkout-change-address-btn"
                        >
                          Change address
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Street address */}
                  <FieldWithError
                    error={errors.delivery_street}
                    inputRef={(el) => (fieldRefs.current.delivery_street = el)}
                  >
                    <input
                      value={checkout.delivery_street}
                      onChange={(e) => {
                        setCheckout({ ...checkout, delivery_street: e.target.value });
                        if (errors.delivery_street) setErrors({ ...errors, delivery_street: undefined });
                      }}
                      data-testid="checkout-address-street"
                      className={`w-full px-4 py-3 rounded-xl border outline-none transition-colors ${errors.delivery_street ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'}`}
                      placeholder="Street address (e.g. 123 Main St, Apt 4)"
                    />
                  </FieldWithError>

                  {/* City + State + ZIP — 3 columns on desktop, stacks on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_140px] gap-3">
                    <FieldWithError
                      error={errors.delivery_city}
                      inputRef={(el) => (fieldRefs.current.delivery_city = el)}
                    >
                      <input
                        value={checkout.delivery_city}
                        onChange={(e) => {
                          setCheckout({ ...checkout, delivery_city: e.target.value });
                          if (errors.delivery_city) setErrors({ ...errors, delivery_city: undefined });
                        }}
                        data-testid="checkout-address-city"
                        className={`w-full px-4 py-3 rounded-xl border outline-none transition-colors ${errors.delivery_city ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'}`}
                        placeholder="City"
                      />
                    </FieldWithError>
                    <FieldWithError
                      error={errors.delivery_state}
                      inputRef={(el) => (fieldRefs.current.delivery_state = el)}
                    >
                      {/* iter91 — show full state names in the open
                          dropdown (so tourists can find their state by
                          name) but only the 2-letter code on the
                          selected field. The trick is a small absolute
                          overlay that paints the code on top of the
                          native select's own text. Pointer events pass
                          through so the dropdown still opens. */}
                      <div className="relative">
                        <select
                          value={checkout.delivery_state}
                          onChange={(e) => {
                            setCheckout({ ...checkout, delivery_state: e.target.value });
                            if (errors.delivery_state) setErrors({ ...errors, delivery_state: undefined });
                          }}
                          data-testid="checkout-address-state"
                          className={`w-full px-3 py-3 rounded-xl border outline-none transition-colors bg-white ${errors.delivery_state ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'} ${checkout.delivery_state ? 'text-transparent' : 'text-slate-400'}`}
                        >
                          <option value="">State</option>
                          {US_STATES.map((s) => (
                            <option key={s.code} value={s.code}>{s.name}</option>
                          ))}
                        </select>
                        {checkout.delivery_state && (
                          <span
                            data-testid="checkout-address-state-display"
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-900 font-semibold"
                          >
                            {checkout.delivery_state}
                          </span>
                        )}
                      </div>
                    </FieldWithError>
                    <FieldWithError
                      error={errors.delivery_zip || (deliveryZipLookup.status === 'error' ? deliveryZipLookup.error : undefined)}
                      inputRef={(el) => (fieldRefs.current.delivery_zip = el)}
                    >
                      <input
                        value={checkout.delivery_zip}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                          setCheckout({ ...checkout, delivery_zip: v });
                          if (errors.delivery_zip) setErrors({ ...errors, delivery_zip: undefined });
                        }}
                        data-testid="checkout-address-zip"
                        inputMode="numeric"
                        maxLength={5}
                        className={`w-full px-4 py-3 rounded-xl border outline-none transition-colors ${errors.delivery_zip || deliveryZipLookup.status === 'error' ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-gold-500'}`}
                        placeholder="ZIP code"
                      />
                    </FieldWithError>
                  </div>

                  {/* Resolved city/state confirmation pill */}
                  {deliveryZipLookup.status === 'ok' && (
                    <div
                      data-testid="checkout-zip-resolved"
                      className="-mt-1 inline-flex items-center gap-1.5 text-[12px] text-slate-600 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full"
                    >
                      ZIP {checkout.delivery_zip} → {deliveryZipLookup.city}, {deliveryZipLookup.state}
                    </div>
                  )}

                  {/* Eligibility error (out-of-range / mismatch / un-geocodable) */}
                  {deliveryEligibility && deliveryEligibility.eligible === false && (
                    <p
                      data-testid="checkout-delivery-error"
                      className="text-[12.5px] text-rose-600 leading-snug"
                    >
                      {deliveryEligibility.error_message}
                    </p>
                  )}
                </div>
              )}
              {/* Delivery-radius eligibility indicator — green pill when
                  the resolved customer ZIP is within the restaurant's
                  radius, red banner when outside. Hidden until a ZIP has
                  been detected (so the customer doesn't see green flash
                  while still typing the street name). */}
              {checkout.order_type === 'delivery'
                && deliveryEligibility
                && !deliveryEligibility.skipped
                && deliveryEligibility.eligible === true && (
                <div
                  data-testid="checkout-delivery-in-range"
                  className="-mt-2 inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 0 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0z" />
                  </svg>
                  Within delivery range ({deliveryEligibility.distance_miles} mi of {deliveryEligibility.radius_miles} mi limit)
                </div>
              )}

              <textarea
                value={checkout.notes}
                onChange={(e) => setCheckout({ ...checkout, notes: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none resize-none"
                rows={2}
                placeholder={t('checkout.notes_optional')}
                data-testid="checkout-notes"
              />
            </div>

            {/* Notification channels */}
            <div className="mb-5" data-testid="checkout-notify-channels">
              <p className="text-sm font-semibold text-slate-700 mb-2">{t("checkout.channels_title")}</p>
              <p className="text-xs text-slate-500 mb-3">{t("checkout.channels_subtitle")}</p>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  // Filter to only the channels the restaurant has enabled
                  // AND marked visible to customers. If the public endpoint
                  // hasn't returned yet (null) we render everything to
                  // avoid a frame of empty options. An empty object means
                  // the restaurant has nothing enabled — show nothing.
                  const allChannels = [
                    { key: 'whatsapp', label: t('checkout.channel_whatsapp'), icon: MessageCircle },
                    { key: 'sms', label: t('checkout.channel_sms'), icon: MessageSquare },
                    { key: 'email', label: t('checkout.channel_email'), icon: Mail },
                  ];
                  const allowed = restaurantChannels === null
                    ? allChannels
                    : allChannels.filter((c) => restaurantChannels?.[c.key]?.enabled);
                  return allowed;
                })().map(({ key, label, icon: Icon }) => {
                  const active = checkout.notify_channels.includes(key);
                  // Email button stays clickable even when the email field
                  // is still empty — selecting it surfaces the inline
                  // "Please enter a valid email address" validation
                  // instead of leaving a confusing greyed-out button.
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        const set = new Set(checkout.notify_channels);
                        if (set.has(key)) {
                          set.delete(key);
                        } else {
                          set.add(key);
                        }
                        const next = Array.from(set);
                        setCheckout({ ...checkout, notify_channels: next });
                        if (next.length > 0 && errors.notify_channels) {
                          setErrors({ ...errors, notify_channels: undefined });
                        }
                        // If the customer just picked Email and the email
                        // input is empty, scroll/focus it so they can fill
                        // it in without hunting for the field.
                        if (key === 'email' && !set.has('email')) return;
                        if (key === 'email' && !checkout.customer_email) {
                          setTimeout(() => {
                            const el = fieldRefs.current?.customer_email
                              || document.querySelector('[data-testid="checkout-email"]');
                            if (el) {
                              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              el.focus();
                            }
                          }, 150);
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-all ${
                        active
                          ? 'bg-gold-500 text-white border-gold-500 shadow-sm'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-gold-400'
                      }`}
                      data-testid={`notify-channel-${key}`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
              {checkout.notify_channels.includes('email') && !checkout.customer_email && (
                <p className="text-xs text-amber-600 mt-2">{t('checkout.channel_email_needed')}</p>
              )}
              {errors?.notify_channels && (
                <p
                  data-testid="checkout-notify-channels-error"
                  className="mt-2 text-xs font-semibold text-rose-600"
                >
                  {errors.notify_channels}
                </p>
              )}
            </div>

            {/* Payment method */}
            <PaymentTrustBadges />
            <div className="grid grid-cols-2 gap-2 mb-2" data-testid="payment-method-picker">
              <button
                onClick={() => stripeReady && setCheckout({...checkout, payment_method: 'card'})}
                data-testid="payment-method-card"
                disabled={!stripeReady}
                aria-disabled={!stripeReady}
                title={stripeReady ? undefined : 'Online card payments are not available for this restaurant yet.'}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-colors ${
                  !stripeReady
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed line-through decoration-slate-300'
                    : checkout.payment_method === 'card'
                      ? 'bg-gold-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                <CreditCard className="w-4 h-4" /> {t('checkout.card_stripe')}
              </button>
              <button onClick={() => setCheckout({...checkout, payment_method: 'cash'})}
                data-testid="payment-method-cash"
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-colors ${checkout.payment_method === 'cash' ? 'bg-gold-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                💵 {t("checkout.cash")}
              </button>
            </div>
            {!stripeReady && (
              <p
                data-testid="checkout-card-unavailable-hint"
                className="mb-4 text-xs text-slate-500 italic"
              >
                Online card payments are not available for this restaurant yet. Please choose another payment method.
              </p>
            )}
            {stripeReady && <div className="mb-4" />}

            <button
              onClick={placeOrder}
              disabled={submitting}
              ref={(el) => (fieldRefs.current['place-order-btn'] = el)}
              data-testid="place-order-btn"
              className="w-full py-4 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-2xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? t('checkout.placing_order') : t('checkout.place_order', { total: grandTotal.toFixed(2) })}
            </button>

            {/* Inline error states for blocker conditions + payment method errors.
                Rendered AFTER the button so it's right where the customer is
                looking when they click. */}
            {errors.payment_method && (
              <div
                data-testid="checkout-payment-error"
                className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm"
              >
                <svg className="w-4 h-4 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{errors.payment_method}</span>
              </div>
            )}
            {errors._blocker && (
              <div
                data-testid="checkout-blocker-banner"
                className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm"
              >
                <svg className="w-4 h-4 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{errors._blocker}</span>
              </div>
            )}
            {/* "Complete the required fields…" hint — only when at least one
                FIELD-level error is present (not for restaurant-level blockers). */}
            {Object.keys(errors).some((k) => !['_blocker', 'payment_method'].includes(k) && errors[k]) && (
              <p
                data-testid="checkout-required-hint"
                className="text-center text-xs text-rose-600 mt-3"
              >
                Complete the required fields above to place your order.
              </p>
            )}
            <p className="text-center text-xs text-slate-400 mt-3">{t('checkout.powered_by')}</p>
          </div>
        </div>
      )}

      {/* Item detail modal (photo + modifiers + notes) */}
      {detailItem && (
        <ItemDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onAddToCart={(configured) => addConfiguredItem(configured)}
        />
      )}

      {/* Reviews modal */}
      {showReviews && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowReviews(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reviews-modal">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-gold-500 text-gold-500" />
                <span className="font-bold">{reviews.average.toFixed(1)}</span>
                <span className="text-slate-400 text-sm">({reviews.count} {reviews.count === 1 ? 'review' : 'reviews'})</span>
              </div>
              <button onClick={() => setShowReviews(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              {reviews.items.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No reviews yet — be the first!</p>
              ) : reviews.items.map((r) => (
                <div key={r.id} className="border-b border-slate-100 pb-3 last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-slate-900">{r.customer_name}</p>
                    <div className="flex">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'fill-gold-500 text-gold-500' : 'text-slate-200'}`} />
                      ))}
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 leading-relaxed">{r.comment}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicMenu;

// ──────────────────────────────────────────────────────────────────────────
// FieldWithError — thin wrapper that renders an inline error message and an
// alert icon under any checkout input. Keeps the existing field markup
// untouched (just clones the child to attach the ref) so the visual design
// stays consistent across all inputs.
// ──────────────────────────────────────────────────────────────────────────
const FieldWithError = ({ children, error, inputRef }) => {
  // Clone the child element to inject our ref so the parent component can
  // call .focus() / .scrollIntoView() on validation failure.
  const child = React.cloneElement(children, { ref: inputRef });
  return (
    <div>
      {child}
      {error && (
        <div
          className="mt-1.5 flex items-start gap-1.5 text-[12.5px] text-rose-600"
          data-testid={`field-error-${(children?.props?.['data-testid'] || '').replace('checkout-', '')}`}
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
